import React, { forwardRef, useEffect, useCallback, useRef, useState } from "react"
import { RemoveScroll } from "react-remove-scroll"
import { AspectRatio, Box, Button, Slide, Flex, Heading, Text } from "@chakra-ui/react"
import Link from "@app/components/Link"
import { useGlobalContext } from "@app/providers/global"
import { useConfigContext } from "@app/providers/config"

import { useAppContext } from "@app/providers/app"
import Icon from "@app/components/Icon"
import ResolvedImage from "@app/components/ResolvedImage"
import { useRoutes } from "@app/hooks/useRoutes"
import { useColorPreset } from "@app/hooks/useColorPreset"

type Props = {
  items: GatsbyTypes.SanityObjectMobileLevel1["items"]
  footerItems: GatsbyTypes.SanityObjectMobileLevel1["items"]
  isOpen: boolean
}

type IndexObject = {
  index: { index: number; title: string }
  subIndex: { index: number; title: string }
  setSubIndex: React.Dispatch<React.SetStateAction<{ index: number; title: string }>>
  setIndex: React.Dispatch<React.SetStateAction<{ index: number; title: string }>>
  level: number
  setLevel: React.Dispatch<React.SetStateAction<number>>
  onClose?: () => void
}

type NavBody = {
  items: GatsbyTypes.SanityObjectMobileLevel1["items"]
  indexes: IndexObject
  footerItems: GatsbyTypes.SanityObjectMobileSecondary["items"]
  drawerHeaderHeight: string
}

type NavBodyItem = {
  item: NonNullable<GatsbyTypes.SanityObjectMobileLevel1["items"]>[number]
  index: number
  indexes: IndexObject
}

type NavFooter = {
  parentLength: number
  footerItems: GatsbyTypes.SanityObjectMobileSecondary["items"]
  indexes: IndexObject
}

type NavSubmenu = {
  item: GatsbyTypes.SanityObjectMobileLevel2
  indexes: IndexObject
}

type NavSubmenuItem = {
  item: NonNullable<GatsbyTypes.SanityObjectMobileLevel2["items"]>[number]
  index: number
  indexes: IndexObject
}

type FeaturedImage = {
  featuredImage: NonNullable<GatsbyTypes.SanityObjectMobileLevel2["featuredImage"]>
  featuredImageUrl: { url: string; title?: string } | null
  indexes: IndexObject
}

type NavThirdMenuItem = {
  item: NonNullable<GatsbyTypes.SanityObjectMobileLevel2["items"]>[number]
  indexes: IndexObject
}

type ThirdMenuFeaturedImage = {
  item: GatsbyTypes.SanityObjectMobileLevel2
  featuredImageUrl: { url: string; title: string } | null
  indexes: IndexObject
}

const NavigationHeaderMenu: React.FC<Props> = ({ items, footerItems, isOpen }) => {
  const { dispatch, navBarRef } = useAppContext()
  const { additionalBack } = useGlobalContext()
  const [index, setIndex] = useState({ index: -1, title: "" })
  const [subIndex, setSubIndex] = useState({ index: -1, title: "" })
  const [level, setLevel] = useState(0)
  const drawerRef = useRef(null)
  const drawerHeaderHeight = "4rem"
  const [headerHeight, setHeaderHeight] = useState<number>(0)

  const onClose = useCallback(() => {
    setIndex({ index: -1, title: "" })
    setSubIndex({ index: -1, title: "" })
    setLevel(0)
    dispatch({
      type: "menu",
      payload: false,
    })
  }, [dispatch])

  const calculateHeights = useCallback(() => {
    const navBarBottom = navBarRef?.current?.getBoundingClientRect()?.bottom || 0
    setHeaderHeight(navBarBottom)
  }, [navBarRef])

  useEffect(() => {
    calculateHeights()

    window.addEventListener("resize", calculateHeights)

    return () => {
      window.removeEventListener("resize", calculateHeights)
    }
  }, [calculateHeights])

  useEffect(() => {
    if (isOpen) calculateHeights()
  }, [isOpen, calculateHeights])

  return (
    <>
      {/* Overlay */}
      <Box
        position="fixed"
        zIndex={900}
        top={`${headerHeight}px`}
        left={0}
        width="100%"
        height={`calc(100% - ${headerHeight}px)`}
        background={isOpen ? "blackAlpha.600" : "transparent"}
        pointerEvents={isOpen ? "auto" : "none"}
        cursor="pointer"
        transition="background 0.2s"
        onClick={onClose}
        overscrollBehavior="none"
      />

      {/* Sliding Panel */}
      <Slide
        direction="left"
        in={isOpen}
        style={{
          top: `${headerHeight}px`,
          width: "90%",
          maxWidth: "28.125rem",
          height: `calc(100% - ${headerHeight}px)`,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <RemoveScroll enabled={isOpen} forwardProps>
          <Flex direction="column" height="100%" bg="primary.white" overflowY="auto" color="colours.secondary" boxShadow="md">
            {/* Header */}
            {level !== 0 && (
              <Flex h={drawerHeaderHeight} borderBottom="1px solid" borderBottomColor="grey.lightest" alignItems="center" px={4}>
                <Button
                  display="flex"
                  fontSize="0.75rem"
                  textTransform="uppercase"
                  variant="transparent"
                  h="100%"
                  onClick={() => {
                    if (level === 1) {
                      setIndex({ index: -1, title: "" })
                      setLevel(0)
                    } else if (level === 2) {
                      setSubIndex({ index: -1, title: "" })
                      setLevel(1)
                    }
                  }}
                >
                  {additionalBack}
                </Button>
                <Heading size="h6" as="span" ml="auto" variant="medium">
                  {level === 1 ? index.title : level === 2 ? subIndex.title : ""}
                </Heading>
              </Flex>
            )}

            {/* Body */}
            <Flex flex="1" direction="column" overflowY="auto" minHeight={0}>
              {isOpen && (
                <NavigationHeaderMobileBody
                  ref={drawerRef}
                  items={items}
                  footerItems={footerItems}
                  drawerHeaderHeight={drawerHeaderHeight}
                  indexes={{
                    index,
                    subIndex,
                    setIndex,
                    setSubIndex,
                    level,
                    setLevel,
                    onClose,
                  }}
                />
              )}
              <NavigationHeaderMobileFooter
                parentLength={items?.length}
                footerItems={footerItems}
                indexes={{
                  index,
                  subIndex,
                  setIndex,
                  setSubIndex,
                  level,
                  setLevel,
                  onClose,
                }}
              />
            </Flex>
          </Flex>
        </RemoveScroll>
      </Slide>
    </>
  )
}

const NavigationHeaderMobileBody = forwardRef<HTMLElement, NavBody>(({ footerItems, items, indexes, drawerHeaderHeight }, ref) => {
  const filteredItems = items?.filter(item => item?._type === "objectMobileLevel1")
  const accordionSubIndex = indexes.index.index

  return (
    <Flex flexDirection="column">
      {/* Top-level navigation */}
      <Flex
        ref={ref}
        width="100%"
        height={`calc(100% - ${drawerHeaderHeight})`}
        transform={accordionSubIndex === -1 ? "translateX(0%)" : "translateX(-100%)"}
        transition="transform 0.3s ease"
        flexDirection="column"
        pb={3}
        zIndex={1000}
      >
        {filteredItems?.map((item, index) => <NavigationHeaderMobileItem key={index} item={item} index={index} indexes={indexes} />)}
      </Flex>

      <Box position="absolute" top={drawerHeaderHeight} width="100%" height="100%" overflow="hidden" zIndex={999}>
        {/* Submenus */}
        {items?.map((item, index) => {
          if (!item || item?._type !== "objectMobileLevel1") return null
          const isActive = accordionSubIndex === index

          return (
            <Box
              key={index}
              position="absolute"
              left="0"
              width="100%"
              height={`calc(100% - ${drawerHeaderHeight})`}
              overflowY="auto"
              overflowX="hidden"
              transform={isActive ? "translateX(0%)" : "translateX(100%)"}
              transition="transform 0.3s ease"
              pointerEvents={isActive ? "auto" : "none"}
              visibility={isActive ? "visible" : "hidden"}
            >
              <NavigationHeaderMobileSubmenu item={item} indexes={indexes} />
            </Box>
          )
        })}

        {/* Footer menus */}
        {footerItems?.map((item, index) => {
          if (!item) return null
          const offsetIndex = items.length + index
          const isActive = accordionSubIndex === offsetIndex

          return (
            <Box
              key={offsetIndex}
              position="absolute"
              left="0"
              width="100%"
              height="100%"
              maxHeight="100%"
              overflowY="auto"
              overflowX="hidden"
              transform={isActive ? "translateX(0%)" : "translateX(150%)"}
              transition="transform 0.3s ease"
              pointerEvents={isActive ? "auto" : "none"}
              visibility={isActive ? "visible" : "hidden"}
            >
              <NavigationHeaderMobileThirdMenu item={item} indexes={indexes} />
            </Box>
          )
        })}
      </Box>
    </Flex>
  )
})

const NavigationHeaderMobileItem = ({ item, index, indexes }: NavBodyItem) => {
  const { anyUrlResolver } = useRoutes()
  const resolvedLink = anyUrlResolver(item?.link)
  const badgeText = item?.link?.badgeText
  const badgeBgColor = useColorPreset(item?.link?.badgeBgColor)?.color
  const badgeTextColor = useColorPreset(item?.link?.badgeTextColor)?.color
  const subHeading = item?.link?.subHeading

  const hasChildren = item?.items?.length > 0
  const isLink = resolvedLink != null

  const commonContent = (
    <>
      {item?.image && (
        <Flex width="86px" height="56px">
          <AspectRatio ratio={208 / 340} width="86px">
            <ResolvedImage image={item?.image} borderRadius="2px" loading="lazy" maxImageWidth={86} />
          </AspectRatio>
        </Flex>
      )}

      <Flex
        flexDirection="column"
        gap={0}
        alignItems="flex-start"
        justifyContent="left"
        paddingLeft="4"
        width={item?.image ? "calc(100% - 100px)" : "80%"}
        flexWrap="wrap"
      >
        <Flex flexDirection="row" alignItems="center" justifyContent="left" flexWrap="wrap" gap={2}>
          <Heading size="h6" as="span" textAlign="left" variant="medium" whiteSpace="normal">
            {item?.title}
          </Heading>

          {badgeText ? (
            <Text as="span" size="sm" variant="medium" color={badgeTextColor} bgColor={badgeBgColor} px={1.5} borderRadius="16px">
              {badgeText}
            </Text>
          ) : null}
        </Flex>

        {subHeading ? (
          <Text as="span" size="md" color="primary.midGrey" whiteSpace="normal">
            {subHeading}
          </Text>
        ) : null}
      </Flex>
    </>
  )

  if (hasChildren) {
    return (
      <Button
        height={subHeading ? "auto" : "100%"}
        key={index}
        onClick={() => {
          indexes.setIndex({ index, title: item?.title || "" })
          indexes.setLevel(1)
        }}
        variant="mobileHeaderItemImage"
        justifyContent="flex-start"
        alignItems="center"
      >
        {commonContent}
        <Flex width="48px" height="48px" justifyContent="center" alignItems="center" marginLeft="auto">
          <Icon name="chevronRightMenu" width="auto" height="48px" fill="primary.white" />
        </Flex>
      </Button>
    )
  }

  if (isLink) {
    return (
      <Button
        height={subHeading ? "auto" : "100%"}
        key={index}
        as={Link}
        to={resolvedLink.url}
        onClick={() => {
          indexes.onClose()
        }}
        justifyContent="flex-start"
        alignItems="center"
        variant="mobileHeaderItemImage"
      >
        {commonContent}
      </Button>
    )
  }
  return null
}

const NavigationHeaderMobileFooter = forwardRef<HTMLElement, NavFooter>(({ footerItems, parentLength, indexes }, ref) => {
  const { anyUrlResolver } = useRoutes()
  const {
    settings: { routes },
  } = useConfigContext()
  const { storesText, accountText } = useGlobalContext()
  const accordionSubIndex = indexes.index.index

  return (
    <Flex
      flexDirection="column"
      transform={accordionSubIndex === -1 ? "translateX(0%)" : "translateX(-100%)"}
      transition="transform 0.3s ease"
      borderTop="1px solid"
      borderTopColor="grey.light"
      height="100%"
      zIndex={1000}
    >
      <Flex ref={ref} width="100%" height="100%" flexDirection="column">
        <Button
          h={13}
          as={Link}
          to={routes.DASHBOARD}
          onClick={() => {
            indexes.onClose()
          }}
          variant="transparent"
          justifyContent="flex-start"
          alignItems="center"
          paddingLeft="6"
        >
          <Flex flexDirection="row" justifyContent="center" height="48px" flexWrap="wrap" alignItems="center">
            <Icon name="account" width={6} height={6} />
            <Heading size="h6" pl={3} variant="normal">
              {accountText}
            </Heading>
          </Flex>
        </Button>
        <Button
          h={14}
          as={Link}
          to={routes.STORE}
          onClick={() => {
            indexes.onClose()
          }}
          variant="transparent"
          justifyContent="flex-start"
          alignItems="center"
          paddingLeft="6"
        >
          <Flex flexDirection="row" justifyContent="center" height="48px" flexWrap="wrap" alignItems="center">
            <Icon name="storePin" width={6} height={6} fill="transparent" />
            <Heading size="h6" pl={3} variant="normal">
              {storesText}
            </Heading>
          </Flex>
        </Button>
        {footerItems?.map((item, index) => {
          const resolvedLink = anyUrlResolver(item?.link)
          const offsetIndex = parentLength + index
          return item && item.items && item.items?.length > 0 ? (
            <Button
              as={Button}
              key={offsetIndex}
              onClick={() => {
                indexes.setIndex({ index: offsetIndex, title: item?.title || "" })
                indexes.setLevel(1)
              }}
              justifyContent="flex-start"
              alignItems="center"
              paddingLeft="6"
              _hover={{
                textDecoration: "none",
              }}
            >
              <Flex flexDirection="column" justifyContent="center" height="48px" flexWrap="wrap" alignItems="flex-start">
                <Heading size="h6" as="span" textAlign="left" variant="medium">
                  {item?.title}
                </Heading>
              </Flex>
              <Flex width="48px" height="48px" justifyContent="center" alignItems="center" marginLeft="auto">
                <Icon name="chevronRightMenu" width="auto" height="48px" fill="primary.white" />
              </Flex>
            </Button>
          ) : item && resolvedLink ? (
            <Button
              key={offsetIndex}
              as={Link}
              to={resolvedLink?.url}
              onClick={() => {
                indexes.onClose()
              }}
              justifyContent="flex-start"
              alignItems="center"
              paddingLeft="6"
              _hover={{
                textDecoration: "none",
              }}
            >
              <Flex flexDirection="column" justifyContent="center" height="48px" flexWrap="wrap" alignItems="flex-start">
                <Heading size="h6" textAlign="left" variant="medium">
                  {item?.title}
                </Heading>
              </Flex>
            </Button>
          ) : null
        })}
      </Flex>
    </Flex>
  )
})

const NavigationHeaderMobileSubmenu = forwardRef<HTMLElement, NavSubmenu>(({ item, indexes }, ref) => {
  const { anyUrlResolver } = useRoutes()
  const { items } = item
  const accordionSubIndex = indexes.subIndex.index
  const featuredImageUrl = anyUrlResolver(item?.featuredImage?.link)

  return (
    <Flex flexDirection="column" position="relative" overflowX="hidden" overflowY="auto" height="100%" marginBottom="auto">
      <Flex
        ref={ref}
        position="absolute"
        top="0"
        left="0"
        overflowY="auto"
        overflowX="hidden"
        width="100%"
        height="100%"
        transform={accordionSubIndex === -1 ? "translateX(0%)" : "translateX(-100%)"}
        transition="transform 0.3s ease"
        flexDirection="column"
        zIndex={1}
      >
        {items?.map((child, index) => <NavigationHeaderMobileSubmenuItem key={index} item={child} index={index} indexes={indexes} />)}

        {item?.featuredImage?.image && (
          <NavigationHeaderMobileFeaturedImage featuredImage={item.featuredImage} featuredImageUrl={featuredImageUrl} indexes={indexes} />
        )}
      </Flex>

      {items?.map((child, index) =>
        child?.items?.length > 0 ? (
          <Box
            key={index}
            position="absolute"
            top="0"
            left="0"
            overflowY="auto"
            overflowX="hidden"
            width="100%"
            height="100%"
            maxHeight="100%"
            transform={accordionSubIndex === index ? "translateX(0%)" : "translateX(100%)"}
            transition="transform 0.3s ease"
          >
            <NavigationHeaderMobileThirdMenu item={child} indexes={indexes} />
          </Box>
        ) : null
      )}
    </Flex>
  )
})

const NavigationHeaderMobileSubmenuItem = ({ item, index, indexes }: NavSubmenuItem) => {
  const { anyUrlResolver } = useRoutes()
  const resolvedLink = anyUrlResolver(item?.link)
  const badgeText = item?.link?.badgeText
  const badgeBgColor = useColorPreset(item?.link?.badgeBgColor)?.color
  const badgeTextColor = useColorPreset(item?.link?.badgeTextColor)?.color
  const subHeading = item?.link?.subHeading

  const hasChildren = item?.items?.length > 0
  const isLink = resolvedLink != null

  const content = (
    <>
      {item?.image && (
        <Flex width="86px" height="56px">
          <AspectRatio ratio={208 / 340} width="86px">
            <ResolvedImage image={item?.image} borderRadius="2px" loading="lazy" maxImageWidth={86} />
          </AspectRatio>
        </Flex>
      )}
      <Flex
        flexDirection="column"
        gap={0}
        alignItems="flex-start"
        justifyContent="center"
        paddingLeft={item?.image ? "4" : "0"}
        flexWrap="wrap"
      >
        <Flex height={subHeading ? "auto" : "100%"} flexDirection="row" alignItems="center" justifyContent="left" flexWrap="wrap" gap={2}>
          <Heading size="h6" as="span" textAlign="left" variant="medium" whiteSpace="normal">
            {item?.title}
          </Heading>

          {badgeText ? (
            <Text as="span" size="sm" variant="medium" color={badgeTextColor} bgColor={badgeBgColor} px={1.5} borderRadius="16px">
              {badgeText}
            </Text>
          ) : null}
        </Flex>
        {subHeading ? (
          <Text as="span" size="md" color="primary.midGrey" whiteSpace="normal">
            {subHeading}
          </Text>
        ) : null}
      </Flex>
    </>
  )

  if (hasChildren) {
    return (
      <Button
        minHeight={subHeading ? "70px" : "56px"}
        key={index}
        onClick={() => {
          indexes.setLevel(2)
          indexes.setSubIndex({ index, title: item?.title || "" })
        }}
        variant={item?.image ? "mobileSubmenuItemImage" : "mobileSubmenuItem"}
        display="flex"
        py={0}
      >
        {content}
        <Flex width="48px" height="48px" justifyContent="center" alignItems="center" marginLeft="auto">
          <Icon name="chevronRightMenu" width="auto" height="48px" fill="primary.white" />
        </Flex>
      </Button>
    )
  }

  if (isLink) {
    return (
      <Button
        minHeight={subHeading ? "70px" : "56px"}
        key={index}
        as={Link}
        to={resolvedLink.url}
        onClick={() => {
          indexes.onClose()
        }}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        variant={item?.image ? "mobileSubmenuItemImage" : "mobileSubmenuItem"}
        _hover={{ textDecoration: "none" }}
        paddingY={item?.image ? "12px" : "0"}
      >
        {content}
      </Button>
    )
  }

  return null
}

const NavigationHeaderMobileFeaturedImage = ({ featuredImage, featuredImageUrl, indexes }: FeaturedImage) => {
  const bgColor = useColorPreset(featuredImage?.backgroundColour)?.color

  if (!featuredImage?.image) return null

  return (
    <Box paddingX="6" marginTop="42px" paddingBottom="6">
      <Flex position="relative" width="100%">
        <AspectRatio ratio={262 / 340} width="100%">
          <ResolvedImage image={featuredImage.image} borderRadius="2px" loading="lazy" maxImageWidth={328} />
        </AspectRatio>
        {featuredImageUrl && (
          <Box position="absolute" bottom="0" left="0" width="100%" padding="4">
            <Button
              as={Link}
              to={featuredImageUrl.url}
              onClick={() => indexes.onClose()}
              width="100%"
              height="50px"
              bgColor={bgColor}
              rounded="32px"
            >
              <Heading
                size="h6"
                textAlign="left"
                variant="medium"
                color={featuredImage?.textColour === "light" ? "primary.white" : "primary.midGrey"}
              >
                {featuredImageUrl.title}
              </Heading>
            </Button>
          </Box>
        )}
      </Flex>
    </Box>
  )
}

const NavigationHeaderMobileThirdMenu = forwardRef<HTMLElement, NavSubmenu>(({ item, indexes }, ref) => {
  const { anyUrlResolver } = useRoutes()
  const { items } = item
  const featuredImageUrl = anyUrlResolver(item?.featuredImage?.link)

  return (
    <Flex
      ref={ref}
      flexDirection="column"
      position="relative"
      overflowX="hidden"
      overflowY="auto"
      height="100%"
      marginBottom="auto"
      paddingBottom="6"
      paddingX="6"
    >
      {items?.map((child, index) => <NavigationHeaderMobileThirdMenuItem key={index} item={child} indexes={indexes} />)}

      {item?.featuredImage?.image && (
        <NavigationHeaderMobileThirdMenuFeatured item={item} featuredImageUrl={featuredImageUrl} indexes={indexes} />
      )}
    </Flex>
  )
})

const NavigationHeaderMobileThirdMenuItem = ({ item, indexes }: NavThirdMenuItem) => {
  const { anyUrlResolver } = useRoutes()
  const resolvedLink = anyUrlResolver(item?.link)
  const badgeText = item?.link?.badgeText
  const badgeBgColor = useColorPreset(item?.link?.badgeBgColor)?.color
  const badgeTextColor = useColorPreset(item?.link?.badgeTextColor)?.color
  const subHeading = item?.link?.subHeading

  if (!resolvedLink) return null

  return (
    <Button
      minHeight={subHeading ? "70px" : "56px"}
      as={Link}
      to={resolvedLink?.url || "/"}
      onClick={indexes.onClose}
      display="flex"
      justifyContent="flex-start"
      _hover={{ textDecoration: "none" }}
      variant="mobileSubmenuItem"
      pl="0"
      pr="0"
      paddingY="0"
    >
      {item?.icon && (
        <Flex width="86px" height="56px">
          <AspectRatio ratio={208 / 340} width="86px">
            <ResolvedImage image={item?.icon} borderRadius="2px" loading="lazy" maxImageWidth={86} />
          </AspectRatio>
        </Flex>
      )}

      <Flex flexDirection="column" gap={0} alignItems="flex-start" justifyContent="center">
        <Flex
          height={subHeading ? "auto" : "100%"}
          flexDirection="row"
          justifyContent="left"
          alignItems="center"
          paddingLeft={item?.icon ? "4" : "0"}
          gap={2}
        >
          <Heading size="h6" as="span" textAlign="left" variant="medium" flexBasis="100%" whiteSpace="normal">
            {resolvedLink.title}
          </Heading>

          {badgeText ? (
            <Text as="span" size="sm" variant="medium" color={badgeTextColor} bgColor={badgeBgColor} px={1.5} borderRadius="16px">
              {badgeText}
            </Text>
          ) : null}
        </Flex>

        {subHeading ? (
          <Text as="span" size="md" color="primary.midGrey" whiteSpace="normal">
            {subHeading}
          </Text>
        ) : null}
      </Flex>
    </Button>
  )
}

const NavigationHeaderMobileThirdMenuFeatured = ({ item, featuredImageUrl, indexes }: ThirdMenuFeaturedImage) => {
  const bgColor = useColorPreset(item?.featuredImage?.backgroundColour)?.color

  return (
    <Box marginTop="42px">
      <Flex position="relative" width="100%">
        <AspectRatio ratio={262 / 340} width="100%">
          <ResolvedImage image={item?.featuredImage?.image} borderRadius="2px" loading="lazy" maxImageWidth={328} />
        </AspectRatio>

        {featuredImageUrl && (
          <Box position="absolute" bottom="0" left="0" width="100%" padding="4">
            <Button
              as={Link}
              to={featuredImageUrl.url}
              onClick={indexes.onClose}
              width="100%"
              height="50px"
              bgColor={bgColor}
              rounded="32px"
            >
              <Heading
                size="h6"
                textAlign="left"
                variant="medium"
                color={item?.featuredImage?.textColour === "light" ? "primary.white" : "primary.midGrey"}
              >
                {featuredImageUrl.title}
              </Heading>
            </Button>
          </Box>
        )}
      </Flex>
    </Box>
  )
}

export default React.memo(NavigationHeaderMenu)
